import React, {useState} from "react";
import {Box, Button, CircularProgress, Grid, Paper, TextField, Typography} from "@material-ui/core";
import {RecordContextProvider, ResourceContextProvider, SaveButton, useNotify} from "react-admin";
import {Link as RouterLink} from "react-router-dom";
import {useOptions} from "../../shared/hooks/useOptions";
import {DeliveryForm} from "../../Distribution/Delivery/DeliveryForm";
import {transformDelivery} from "../../Distribution/Delivery/utils";
import {client} from "../../dataProvider";

export const NoteConvertDeliveryForm = ({
                                            file,
                                            convertNote,
                                            busy,
                                            saveDraft,
                                            createDelivery,
                                            createReceiver,
                                            associateDelivery,
                                            remove
                                        }: {
    file: any;
    convertNote: any;
    busy: boolean;
    saveDraft: (data: any) => Promise<any>;
    createDelivery: (data: any) => Promise<any>;
    createReceiver: (data: any) => Promise<any>;
    associateDelivery: (id: number) => void;
    remove: () => void;
}) => {
    const {data: options} = useOptions("deliveries", "POST");
    const [receiverData, setReceiverData] = useState(file.receiver_data || {});
    const notify = useNotify();
    const [calculating, setCalculating] = useState(false);
    const validCoordinate = (value: any, limit: number) => value !== undefined && value !== null &&
        String(value).trim() !== "" && Number.isFinite(Number(value)) && Math.abs(Number(value)) <= limit;
    const hasCoordinates = validCoordinate(receiverData.latitude, 90) && validCoordinate(receiverData.longitude, 180) &&
        !(Number(receiverData.latitude) === 0 && Number(receiverData.longitude) === 0);
    const calculateCoordinates = async () => {
        if (!receiverData.address || !receiverData.province || !(receiverData.locality || receiverData.postal_code)) {
            notify("Inserisci indirizzo, provincia e località oppure CAP prima di calcolare le coordinate", "warning");
            return;
        }
        setCalculating(true);
        try {
            const response = await client("autocomplete/address_latlon", {
                method: "GET", params: {
                    address: receiverData.address, province: receiverData.province,
                    locality: receiverData.locality, postal_code: receiverData.postal_code,
                },
            });
            if (!response || !validCoordinate(response.lat, 90) || !validCoordinate(response.lon, 180) ||
                (Number(response.lat) === 0 && Number(response.lon) === 0)) {
                notify("Nessun risultato trovato. Puoi inserire le coordinate da GMaps.", "warning");
                return;
            }
            setReceiverData((current: any) => ({...current, latitude: response.lat, longitude: response.lon, altitude: response.altitude}));
        } catch {
            notify("Impossibile calcolare le coordinate", "error");
        } finally { setCalculating(false); }
    };
    const openGmaps = () => {
        const query = [receiverData.business_name, receiverData.address, receiverData.locality, receiverData.postal_code, receiverData.province]
            .filter(Boolean).join(" ");
        window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`, "_blank", "noopener,noreferrer");
    };
    // L'inizializzazione resta stabile durante il refetch della stessa bolla.
    const [initialRecord] = useState(() => ({
        delivery_type: file.delivery_type || "NO",
        client: file.client || convertNote.client || null,
        receiver: file.receiver || null,
        sender: convertNote.sender || null,
        starting_point_on_delivery: convertNote.starting_point_on_delivery || null,
        transit_point: convertNote.transit_point || null,
        document_number: file.document_number,
        document_date: file.document_date,
        weight: file.weight,
        volume: file.volume,
        packages_number: file.packages_number,
        cachet: file.cachet,
        address: file.receiver_data?.address || "",
        locality: file.receiver_data?.locality || "",
        province: file.receiver_data?.province || "",
        postal_code: file.receiver_data?.postal_code || "",
        exclude_first_segment: true,
        ...file.delivery_data,
    }));
    const [record, setRecord] = useState(initialRecord);
    const draftValues = (data: any) => {
        const values = {...transformDelivery(data), receiver_data: receiverData};
        for (const field of ["weight", "volume", "packages_number", "cachet", "real_weight", "value", "insurance", "document_date", "delivery_date"]) {
            if (values[field] === "") values[field] = null;
        }
        if (!values.new_service) delete values.new_service;
        return values;
    };
    const save = async (data: any, action: any) => {
        const values = transformDelivery(data);
        return action === "create" ? createDelivery(values) : saveDraft(draftValues(data));
    };
    const addReceiver = async () => {
        if (!hasCoordinates) {
            notify("Inserisci latitudine e longitudine valide prima di creare il destinatario", "warning");
            return;
        }
        const created = await createReceiver(receiverData);
        if (created?.receiver) setRecord((current: any) => ({
            ...current, receiver: created.receiver,
            address: receiverData.address || "", locality: receiverData.locality || "",
            postal_code: receiverData.postal_code || "", province: receiverData.province || "",
        }));
    };
    if (!options) return <Box p={3}><CircularProgress/></Box>;
    const hasExistingDeliveries = !!file.existing_deliveries?.length;
    return <Box p={1}>
        <Grid container spacing={1}>
            <Grid item xs={12} md={hasExistingDeliveries ? 6 : 12}>
                <Paper variant="outlined" style={{height: "100%"}}><Box p={1.5}>
                    <Typography variant="subtitle2">Cliente estratto</Typography>
                    <Box display="flex" flexWrap="wrap" mt={0.5} style={{columnGap: 16, rowGap: 4}}>
                    {[["business_name", "Ragione sociale"], ["address", "Indirizzo"], ["locality", "Località"], ["postal_code", "CAP"], ["province", "Provincia"]].map(([field, label]) =>
                        <Box key={field} minWidth={field === "business_name" || field === "address" ? 140 : 60} style={{overflowWrap: "anywhere"}}>
                            <Typography variant="caption" color="textSecondary" component="span">{label}: </Typography>
                            <Typography variant="body2" component="span">{file.client_data?.[field] || "—"}</Typography>
                        </Box>)}
                    </Box>
                    <Typography variant="caption" color="textSecondary" display="block">
                        {file.client ? `Cliente associato: ${file.client_name}` : "Seleziona il cliente nel form della missione."}
                    </Typography>
                </Box></Paper>
            </Grid>
            {hasExistingDeliveries && <Grid item xs={12} md={6}>
                <Paper variant="outlined" style={{height: "100%"}}><Box p={1.5}>
                    <Typography variant="subtitle2">Missioni già presenti</Typography>
                    {file.existing_deliveries.map((delivery: any) => <Box key={delivery.id} display="flex" alignItems="center"
                        flexWrap="wrap" style={{gap: 4}}>
                        <Button size="small" component={RouterLink} to={`/deliveries/${delivery.id}`} target="_blank"
                            rel="noopener noreferrer">Missione #{delivery.id}</Button>
                        <Typography variant="caption" style={{overflowWrap: "anywhere"}}>
                            {delivery.document_number} · {delivery.document_date} · {delivery.receiver_name}
                        </Typography>
                        <Button size="small" color="primary" disabled={busy} onClick={() => associateDelivery(delivery.id)}>Associa bolla</Button>
                    </Box>)}
                    <Box display="flex" alignItems="center" flexWrap="wrap" style={{gap: 8}}>
                        <Button size="small" color="secondary" disabled={busy} onClick={remove}>Rimuovi bolla</Button>
                        <Typography variant="caption" color="textSecondary">Puoi comunque creare una nuova delivery sotto.</Typography>
                    </Box>
                </Box></Paper>
            </Grid>}
            <Grid item xs={12}>
                <Paper variant="outlined"><Box p={1.5}>
                    <Typography variant="subtitle2">Destinatario estratto</Typography>
                    <Grid container spacing={1}>
                        {[["business_name", "Ragione sociale"], ["address", "Indirizzo"], ["locality", "Località"], ["postal_code", "CAP"], ["province", "Prov."]].map(([field, label]) =>
                            <Grid item xs={field === "business_name" || field === "address" ? 12 : field === "locality" ? 6 : 3}
                                sm={field === "postal_code" || field === "province" ? 3 : 6}
                                md={field === "postal_code" ? 2 : field === "province" ? 1 : 3} key={field}>
                                <TextField fullWidth size="small" margin="dense" label={label} value={receiverData[field] || ""} disabled={busy || calculating}
                                           onChange={(event) => setReceiverData((current: any) => ({
                                               ...current,
                                               [field]: event.target.value
                                           }))}/>
                            </Grid>)}
                    </Grid>
                    {!file.receiver && <>
                        <Grid container spacing={1} alignItems="center">
                            <Grid item xs={12} sm={4} md={3}><TextField fullWidth size="small" margin="dense" label="Incolla da GMaps"
                                disabled={busy || calculating} placeholder="45.4642, 9.1900"
                                onChange={(event) => {
                                    const coords = event.target.value.replace(/\s/g, "").split(",");
                                    if (coords.length === 2 && validCoordinate(coords[0], 90) && validCoordinate(coords[1], 180)) {
                                        setReceiverData((current: any) => ({...current, latitude: coords[0], longitude: coords[1]}));
                                    }
                                }}/></Grid>
                            {[["latitude", "Latitudine", 90], ["longitude", "Longitudine", 180]].map(([field, label, limit]) =>
                                <Grid item xs={6} sm={4} md={2} key={String(field)}><TextField fullWidth required type="number" size="small"
                                    margin="dense" label={label} value={receiverData[field] ?? ""} disabled={busy || calculating}
                                    inputProps={{step: "any", min: -Number(limit), max: Number(limit)}}
                                    onChange={event => setReceiverData((current: any) => ({...current, [field]: event.target.value}))}/></Grid>)}
                        <Grid item xs={12} md={5}><Box display="flex" flexWrap="wrap" style={{gap: 6}}>
                            <Button size="small" variant="outlined" color="primary" disabled={busy || calculating || !receiverData.business_name?.trim() || !hasCoordinates}
                                onClick={addReceiver}>Crea destinatario</Button>
                            <Button size="small" variant="outlined" color="primary" disabled={busy || calculating} onClick={calculateCoordinates}>
                                {calculating ? "Calcolo…" : "Calcola"}</Button>
                            <Button size="small" variant="outlined" color="primary" disabled={busy || calculating} onClick={openGmaps}>GMaps</Button>
                        </Box></Grid>
                        </Grid>
                        {!hasCoordinates && <Typography variant="caption" color="textSecondary">Calcola o inserisci le coordinate per creare il destinatario.</Typography>}
                    </>}
                </Box></Paper>
            </Grid>
        </Grid>
        <Box>
            <ResourceContextProvider value="deliveries">
                <RecordContextProvider value={record}>
                    <DeliveryForm record={record} resource="deliveries" basePath="/deliveries" fieldOptions={options}
                                  canEdit canDelete={false} save={save} saving={busy}
                                  renderToolbar={(formProps) => <Box p={2} display="flex" flexWrap="wrap"
                                                                     style={{gap: 12}}>
                                      <Button variant="outlined" color="primary" disabled={busy || formProps.saving}
                                              onClick={() => saveDraft(draftValues(formProps.form.getState().values))}>
                                          Salva dati
                                      </Button>
                                      <SaveButton
                                          label={file.existing_deliveries?.length ? "Crea nuova delivery" : "Crea delivery"}
                                          redirect="create" submitOnEnter={false}
                                          handleSubmitWithRedirect={formProps.handleSubmitWithRedirect}
                                          saving={busy || formProps.saving} invalid={formProps.invalid}/>
                                  </Box>}/>
                </RecordContextProvider>
            </ResourceContextProvider>
        </Box>
    </Box>;
};
